type Subscriber = (pendingCount: number) => void;

const MUTATING_METHODS = ['POST', 'PUT', 'PATCH', 'DELETE'];

export class PendingRequestTracker {
  private pendingCount = 0;
  private readonly subscribers: Set<Subscriber> = new Set();
  private readonly maxPendingCount = 1000;

  private isMutatingMethod(method: string): boolean {
    return MUTATING_METHODS.includes(method.toUpperCase());
  }

  private safeIncrement(): void {
    if (this.pendingCount >= this.maxPendingCount) {
      console.error(
        `PendingRequestTracker: max pending count (${this.maxPendingCount}) exceeded. Possible counter corruption.`
      );
      return;
    }
    this.pendingCount++;
  }

  private safeDecrement(): void {
    if (this.pendingCount <= 0) {
      console.error(
        'PendingRequestTracker: attempted to decrement below zero. Counter may be corrupted.'
      );
      this.pendingCount = 0;
      return;
    }
    this.pendingCount--;
  }

  incrementIfMutating(method: string): void {
    if (this.isMutatingMethod(method)) {
      this.safeIncrement();
      this.notifySubscribers();
    }
  }

  decrementIfMutating(method: string): void {
    if (this.isMutatingMethod(method)) {
      this.safeDecrement();
      this.notifySubscribers();
    }
  }

  hasPendingRequests(): boolean {
    return this.pendingCount > 0;
  }

  getPendingCount(): number {
    return this.pendingCount;
  }

  subscribe(subscriber: Subscriber): () => void {
    this.subscribers.add(subscriber);
    subscriber(this.pendingCount);
    return () => {
      this.subscribers.delete(subscriber);
    };
  }

  private notifySubscribers(): void {
    this.subscribers.forEach(subscriber => subscriber(this.pendingCount));
  }
}

export const globalPendingRequestTracker = new PendingRequestTracker();
